import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  MenuItem,
  useMediaQuery,
  Divider,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  CircularProgress,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  GridOn as GridOnIcon,
  Add as AddIcon,
  AccountCircle as AccountIcon,
  CheckCircleOutline as CloseIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Save as SaveIcon,
  MyLocation as MyLocationIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import { Chip } from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import { getUserFromToken } from "../../api/authApi";

const API_URL = `${process.env.REACT_APP_API_URL}/api/branch`;
const LU_URL = `${process.env.REACT_APP_API_URL}/api/dpt/branch`;

// --- Theme Styles ---
const yellowButton = {
  background: "#FFD600",
  color: "#000000",
  fontWeight: "bold",
  "&:hover": {
    background: "#ffcc00",
    boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
  },
};

const whiteCard = {
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
  borderRadius: "12px",
  color: "#000000",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
};

const whiteInput = {
  "& .MuiOutlinedInput-root": {
    color: "#000000",
    backgroundColor: "#ffffff",
    "& fieldset": { borderColor: "#e0e0e0" },
    "&:hover fieldset": { borderColor: "#FFD600" },
    "&.Mui-focused fieldset": { borderColor: "#FFD600" },
  },
  "& .MuiInputLabel-root": { color: "#666666" },
};

const tableCell = {
  color: "#000000",
  borderBottom: "1px solid #e0e0e0",
};

// Password validation function
const validatePassword = (password) => {
  const errors = [];

  if (password.length < 8) {
    errors.push("At least 8 characters");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("At least one uppercase letter");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("At least one lowercase letter");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("At least one number");
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("At least one special character");
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

// Get system status color
const getSystemStatusColor = (systemStatus) => {
  const status = systemStatus?.toLowerCase() || "unknown";
  switch (status) {
    case 'active':
      return "#00e676";
    case 'lagging':
      return "#ff9800";
    case 'intermittent':
      return "#ff9800";
    case 'down':
      return "#ff4444";
    default:
      return "#999999";
  }
};

// Get system status icon
const getSystemStatusIcon = (systemStatus) => {
  const status = systemStatus?.toLowerCase() || "unknown";
  switch (status) {
    case 'active':
      return <CheckCircleIcon />;
    case 'lagging':
      return <WarningIcon />;
    case 'intermittent':
      return <WarningIcon />;
    case 'down':
      return <ErrorIcon />;
    default:
      return <ComputerIcon />;
  }
};

const BranchDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  // Get user info from token
  const user = useMemo(() => getUserFromToken(), []);
  const currentUsername = user?.username || "";
  const currentRole = "branch";

  // Refs
  const messagesEndRef = useRef(null);
  const editInputRef = useRef(null);

  // State Management
  const [profile, setProfile] = useState({});
  const [tickets, setTickets] = useState([]);
  const [newTicket, setNewTicket] = useState({
    status: "Down",
    comment: "",
    ticketStatus: "Open",
    CCTNumber: "",
  });
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [conversationInput, setConversationInput] = useState("");
  const [commentsArray, setCommentsArray] = useState([]);
  const [expandedComment, setExpandedComment] = useState(true);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editMessageText, setEditMessageText] = useState("");
  const [isTicketOpen, setIsTicketOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Down");
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isSystemDialogOpen, setIsSystemDialogOpen] = useState(false);
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editForm, setEditForm] = useState({
    primaryContact: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Password visibility and validation states
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [problemType, setProblemType] = useState("ETC");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [shiftType, setShiftType] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const [systemStatus, setSystemStatus] = useState('Unknown');
  const [systemProblem, setSystemProblem] = useState('Ok');
  const [duration, setDuration] = useState(0);
  const [suggestion, setSuggestion] = useState("Unknown");
  const [isProblemSaved, setIsProblemSaved] = useState(false);

  // Location State
  const [deviceLocation, setDeviceLocation] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Helper function to get location display string from geo object
  const getLocationDisplay = useCallback((profileData) => {
    if (profileData?.geo?.coordinates && profileData.geo.coordinates.length === 2) {
      const [lng, lat] = profileData.geo.coordinates;
      return `${lat?.toFixed(6) || 'N/A'}, ${lng?.toFixed(6) || 'N/A'}`;
    }
    return profileData?.location || "Not Set";
  }, []);

  // Clear password fields function
  const clearPasswordFields = useCallback(() => {
    setPasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordErrors([]);
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }, []);

  // ===== DEFINE showSnackbar ONCE =====
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // ==================== PROBLEM TYPE FUNCTIONS ====================

  const handleProblemTypeChange = useCallback((event) => {
    setProblemType(event.target.value);
  }, []);

  const handleSaveProblemType = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      await axios.put(
        `${API_URL}/status/problem`,
        { problem: problemType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProfile(prev => ({
        ...prev,
        problem: problemType
      }));

      setIsProblemSaved(true);
      showSnackbar("Problem type updated successfully!", "success");
      setIsStatusDialogOpen(false);
    } catch (err) {
      console.error("Error updating problem type:", err);
      showSnackbar("Failed to update problem type", "error");
    }
  }, [problemType, showSnackbar]);

  // Load current problem type from profile
  useEffect(() => {
    if (profile.problem) {
      setProblemType(profile.problem);
      setIsProblemSaved(true);
    }
  }, [profile.problem]);

  // Reset isProblemSaved when dialog opens
  useEffect(() => {
    if (isStatusDialogOpen) {
      setIsProblemSaved(false);
    }
  }, [isStatusDialogOpen]);

  // ==================== DATA FETCHING FUNCTIONS ====================

  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const res = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data.branch || res.data;

      if (data.geo?.coordinates) {
        const [lng, lat] = data.geo.coordinates;
        data.locationDisplay = `${lat?.toFixed(6) || 'N/A'}, ${lng?.toFixed(6) || 'N/A'}`;
      } else {
        data.locationDisplay = "Not Set";
      }

      setProfile(data);
      setConnectionStatus(data.status || "N/A");
      setEditForm((prev) => ({
        ...prev,
        primaryContact: data.primaryContact || "",
      }));
    } catch (err) {
      showSnackbar("Failed to load profile", "error");
    }
  }, [showSnackbar]);

  const fetchLastUpdate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found');
        return;
      }

      const res = await axios.get(`${LU_URL}/lastUpdate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastUpdate(res.data.lastUpdate || "—");
      setShiftType(res.data.shiftType || "Normal");
      setServerStatus(res.data.serverStatus || "Unknown");
      setSystemStatus(res.data.systemStatus || "Unknown");
      setSystemProblem(res.data.systemProblem || "Ok");
      setDuration(res.data.duration || 0);
      setSuggestion(res.data.suggestion || "Unknown");
    } catch (err) {
      console.error('Error fetching last update:', err);
      showSnackbar("Registry Sync Failedx2", "error");
    }
  }, [showSnackbar]);

  const fetchTickets = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(
        Array.isArray(res.data.tickets) ? res.data.tickets : res.data || [],
      );
    } catch (err) {
      showSnackbar("Failed to fetch tickets", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  // ==================== LOCATION FUNCTIONS ====================

  const getCurrentLocation = useCallback(() => {
    setIsGettingLocation(true);

    if (!navigator.geolocation) {
      showSnackbar("Geolocation is not supported by your browser", "error");
      setIsGettingLocation(false);
      return Promise.reject(new Error("Geolocation not supported"));
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          const location = {
            lat: latitude,
            lng: longitude,
            accuracy: accuracy,
            timestamp: new Date().toISOString(),
          };
          setDeviceLocation(location);
          showSnackbar(`Location obtained: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`, "success");
          setIsGettingLocation(false);
          resolve(location);
        },
        (error) => {
          let errorMessage = "Unable to get location";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Location permission denied. Please enable location access.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information is unavailable";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out";
              break;
            default:
              errorMessage = "An unknown error occurred";
          }
          showSnackbar(errorMessage, "error");
          setIsGettingLocation(false);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  }, [showSnackbar]);

  const updateLocationToDatabase = useCallback(async (locationData) => {
    setIsUpdatingLocation(true);
    const token = localStorage.getItem("token");

    try {
      const response = await axios.put(
        `${API_URL}/update-location`,
        {
          geo: {
            type: "Point",
            coordinates: [locationData.lng, locationData.lat]
          },
          locationAccuracy: locationData.accuracy,
          lastLocationUpdate: locationData.timestamp,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const updatedProfile = {
          ...profile,
          geo: {
            type: "Point",
            coordinates: [locationData.lng, locationData.lat]
          },
          locationAccuracy: locationData.accuracy,
          lastLocationUpdate: locationData.timestamp,
          locationDisplay: `${locationData.lat.toFixed(6)}, ${locationData.lng.toFixed(6)}`
        };
        setProfile(updatedProfile);
        showSnackbar("Location updated successfully to database!", "success");
        return response.data;
      }
    } catch (error) {
      console.error("Error updating location:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to update location to database",
        "error"
      );
      throw error;
    } finally {
      setIsUpdatingLocation(false);
    }
  }, [profile, showSnackbar]);

  const handleLocationUpdate = useCallback(async () => {
    try {
      const location = await getCurrentLocation();
      if (location) {
        await updateLocationToDatabase(location);
        await fetchProfile();
      }
    } catch (error) {
      console.error("Location update failed:", error);
    }
  }, [getCurrentLocation, updateLocationToDatabase, fetchProfile]);

  const handleUpdateLocationClick = useCallback(() => {
    setIsConfirmDialogOpen(true);
  }, []);

  const confirmAndUpdateLocation = useCallback(async () => {
    setIsConfirmDialogOpen(false);
    await handleLocationUpdate();
  }, [handleLocationUpdate]);

  // ==================== COMMENT MANAGEMENT FUNCTIONS ====================

  const parseCommentsToArray = useCallback((comment) => {
    if (!comment) return [];

    const commentBlocks = [];
    const lines = comment.split("\n");
    let currentComment = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.startsWith("#")) {
        if (currentComment) {
          commentBlocks.push(currentComment);
        }
        currentComment = line;
      } else if (currentComment) {
        currentComment += "\n" + line;
      } else if (line.trim()) {
        commentBlocks.push(line);
      }
    }
    if (currentComment) {
      commentBlocks.push(currentComment);
    }

    const parsedComments = [];
    let counter = 0;

    for (let block of commentBlocks) {
      const firstLine = block.split("\n")[0];
      const match = firstLine.match(/^#(.*?)\[(.*?)\] (.*?): (.*)$/);

      if (match) {
        const lines = block.split("\n");
        const remainingLines = lines.slice(1);
        const fullMessage =
          match[4] +
          (remainingLines.length > 0 ? "\n" + remainingLines.join("\n") : "");

        parsedComments.push({
          id: `comment-${Date.now()}-${counter++}`,
          username: match[1],
          role: match[2],
          timestamp: match[3],
          message: fullMessage,
          raw: block,
          isEditing: false,
        });
      } else if (block.trim()) {
        parsedComments.push({
          id: `comment-${Date.now()}-${counter++}`,
          username: "System",
          role: "system",
          timestamp: new Date().toLocaleString(),
          message: block,
          raw: block,
          isEditing: false,
        });
      }
    }

    return parsedComments;
  }, []);

  const formatCommentsToString = useCallback((commentsArray) => {
    return commentsArray.map((comment) => comment.raw).join("\n");
  }, []);

  const addNewComment = useCallback(
    (commentsArray, newMessage, username, role) => {
      const timestamp = new Date().toLocaleString();
      const rawComment = `#${username}[${role}] ${timestamp}: ${newMessage}`;
      const newComment = {
        id: `comment-${Date.now()}-${commentsArray.length}`,
        username,
        role,
        timestamp,
        message: newMessage,
        raw: rawComment,
        isEditing: false,
      };
      return [...commentsArray, newComment];
    },
    [],
  );

  const updateComment = useCallback(
    (commentsArray, commentId, newMessage, username, role) => {
      const timestamp = new Date().toLocaleString();
      const oldComment = commentsArray.find((c) => c.id === commentId);
      const isEdited = oldComment && oldComment.message !== newMessage;
      const updatedRaw = `#${username}[${role}] ${timestamp}: ${newMessage}${isEdited ? " (edited)" : ""
        }`;
      return commentsArray.map((comment) =>
        comment.id === commentId
          ? {
            ...comment,
            message: newMessage,
            raw: updatedRaw,
            timestamp,
            isEditing: false,
          }
          : comment,
      );
    },
    [],
  );

  const deleteComment = useCallback((commentsArray, commentId) => {
    return commentsArray.filter((comment) => comment.id !== commentId);
  }, []);

  // Load comments when ticket comment changes
  useEffect(() => {
    if (newTicket.comment) {
      setCommentsArray(parseCommentsToArray(newTicket.comment));
    } else {
      setCommentsArray([]);
    }
  }, [newTicket.comment, parseCommentsToArray]);

  useEffect(() => {
    if (expandedComment && commentsArray.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [commentsArray, expandedComment, scrollToBottom]);

  useEffect(() => {
    if (editingCommentId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingCommentId]);

  // Memoized values
  const filteredTickets = useMemo(() => {
    if (!Array.isArray(tickets)) return [];

    return tickets.filter((t) => {
      const matchesSearch =
        t.comment?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        t.CCTNumber?.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchesFilter =
        statusFilter === "All" ? true : t.ticketStatus === statusFilter;
      return matchesSearch && matchesFilter;
    });
  }, [tickets, debouncedSearch, statusFilter]);

  // Initial data load
  useEffect(() => {
    fetchProfile();
    fetchLastUpdate();
    fetchTickets();
  }, [fetchProfile, fetchTickets, fetchLastUpdate]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const getStatusColor = useCallback(() => {
    if (connectionStatus === "Active") return "#44ff44";
    if (connectionStatus === "Intermittent") return "#FFD600";
    return "#ff0000";
  }, [connectionStatus]);

  const downloadExcel = useCallback(() => {
    const dataToExport = filteredTickets.map((t) => ({
      Status: t.ticketStatus,
      CCTNumber: t.CCTNumber,
      OpenedAt: new Date(t.openedAt).toLocaleString(),
      ClosedAt: t.closedAt ? new Date(t.closedAt).toLocaleString() : "Pending",
      Technician: t.assignedTechnician,
      TechContact: t.techprimaryContact,
      comment: t.comment,
      Branch: profile?.name || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BranchLogs");
    XLSX.writeFile(
      workbook,
      `Branch_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
    showSnackbar("Excel exported successfully");
  }, [filteredTickets, profile?.name, showSnackbar]);

  const handleProfileChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));

    // Validate password when newPassword changes
    if (name === 'newPassword') {
      const validation = validatePassword(value);
      setPasswordErrors(validation.errors);
    }
  }, []);

  const handleOpenCreate = useCallback(() => {
    if (connectionStatus === "Active") {
      showSnackbar(
        "System is ACTIVE. No need to create trouble ticket.",
        "info",
      );
      return;
    }
    setEditId(null);
    setCommentsArray([]);
    setConversationInput("");
    setExpandedComment(true);
    setEditingCommentId(null);
    setNewTicket({
      status: connectionStatus,
      comment: "",
      ticketStatus: "Open",
      CCTNumber: "",
    });
    setIsTicketOpen(true);
  }, [connectionStatus, showSnackbar]);

  const handleSaveEdit = useCallback(
    (commentId) => {
      if (!editMessageText.trim()) {
        showSnackbar("Message cannot be empty", "warning");
        return;
      }

      if (editMessageText.includes("#")) {
        showSnackbar(
          "The '#' character is reserved for comment separation and cannot be used in messages",
          "warning",
        );
        return;
      }

      const comment = commentsArray.find((c) => c.id === commentId);
      if (comment) {
        const updatedComments = updateComment(
          commentsArray,
          commentId,
          editMessageText,
          comment.username,
          comment.role,
        );
        const updatedCommentString = formatCommentsToString(updatedComments);

        setCommentsArray(updatedComments);
        setNewTicket((prev) => ({
          ...prev,
          comment: updatedCommentString,
        }));
        setEditingCommentId(null);
        setEditMessageText("");
        showSnackbar("Comment edited successfully", "success");
      }
    },
    [
      editMessageText,
      commentsArray,
      updateComment,
      formatCommentsToString,
      showSnackbar,
    ],
  );

  const handleOpenEdit = useCallback(
    (ticket) => {
      setEditId(ticket._id);
      const enforcedTicketStatus =
        connectionStatus === "Active" ? "Closed" : "Open";
      const currentcomment = ticket.comment || "";
      setNewTicket({
        status: connectionStatus,
        comment: currentcomment,
        ticketStatus: enforcedTicketStatus,
        CCTNumber: ticket.CCTNumber || "",
      });
      setConversationInput("");
      setExpandedComment(true);
      setEditingCommentId(null);
      setIsTicketOpen(true);
    },
    [connectionStatus],
  );

  const handleSendMessage = useCallback(() => {
    if (!conversationInput.trim()) {
      showSnackbar("Please enter a message", "warning");
      return;
    }

    if (conversationInput.includes("#")) {
      showSnackbar(
        "The '#' character is reserved for comment separation and cannot be used in messages",
        "warning",
      );
      return;
    }

    const updatedComments = addNewComment(
      commentsArray,
      conversationInput,
      currentUsername,
      currentRole,
    );
    const updatedCommentString = formatCommentsToString(updatedComments);

    setCommentsArray(updatedComments);
    setNewTicket((prev) => ({
      ...prev,
      comment: updatedCommentString,
    }));
    setConversationInput("");
    showSnackbar("Message sent successfully", "success");
  }, [
    conversationInput,
    commentsArray,
    currentUsername,
    currentRole,
    addNewComment,
    formatCommentsToString,
    showSnackbar,
  ]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "#") {
        e.preventDefault();
        showSnackbar(
          "The '#' character is reserved for comment separation and cannot be used",
          "warning",
        );
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, showSnackbar],
  );

  const handleEditKeyPress = useCallback(
    (e, commentId) => {
      if (e.key === "#") {
        e.preventDefault();
        showSnackbar(
          "The '#' character is reserved for comment separation and cannot be used",
          "warning",
        );
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSaveEdit(commentId);
      }
    },
    [handleSaveEdit, showSnackbar],
  );

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value.includes("#") && !conversationInput.includes("#")) {
        showSnackbar(
          "The '#' character is reserved for comment separation and cannot be used",
          "warning",
        );
        return;
      }
      setConversationInput(value);
    },
    [conversationInput, showSnackbar],
  );

  const handleEditInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value.includes("#") && !editMessageText.includes("#")) {
        showSnackbar(
          "The '#' character is reserved for comment separation and cannot be used",
          "warning",
        );
        return;
      }
      setEditMessageText(value);
    },
    [editMessageText, showSnackbar],
  );

  const startEditing = useCallback((comment) => {
    if (comment.role === "branch") {
      setEditingCommentId(comment.id);
      setEditMessageText(comment.message);
    }
    handleCommentMenuClose();
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingCommentId(null);
    setEditMessageText("");
  }, []);

  const handleCommentMenuOpen = useCallback((event, commentId) => {
    event.stopPropagation();
    setSelectedCommentId(commentId);
    setCommentMenuAnchor(event.currentTarget);
  }, []);

  const handleCommentMenuClose = useCallback(() => {
    setCommentMenuAnchor(null);
    setSelectedCommentId(null);
  }, []);

  const handleDeleteComment = useCallback(() => {
    if (
      selectedCommentId &&
      window.confirm("Are you sure you want to delete this comment?")
    ) {
      const updatedComments = deleteComment(commentsArray, selectedCommentId);
      const updatedCommentString = formatCommentsToString(updatedComments);

      setCommentsArray(updatedComments);
      setNewTicket((prev) => ({
        ...prev,
        comment: updatedCommentString,
      }));
      showSnackbar("Comment deleted successfully", "success");
    }
    handleCommentMenuClose();
  }, [
    selectedCommentId,
    commentsArray,
    deleteComment,
    formatCommentsToString,
    showSnackbar,
    handleCommentMenuClose,
  ]);

  const handleSaveTicket = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    let finalCommentString = newTicket.comment;

    if (conversationInput.trim()) {
      if (conversationInput.includes("#")) {
        showSnackbar(
          "The '#' character is reserved for comment separation and cannot be used",
          "warning",
        );
        return;
      }
      const updatedComments = addNewComment(
        commentsArray,
        conversationInput,
        currentUsername,
        currentRole,
      );
      finalCommentString = formatCommentsToString(updatedComments);
    }

    const payload = {
      ...newTicket,
      comment: finalCommentString || "",
      status: connectionStatus,
    };

    try {
      if (editId) {
        await axios.put(`${API_URL}/ticket/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar("Ticket updated successfully.", "success");
        setIsTicketOpen(false);
        fetchTickets();
      } else {
        await axios.post(`${API_URL}/create`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showSnackbar("Ticket created successfully.", "success");
        setIsTicketOpen(false);
        fetchTickets();
      }
    } catch (err) {
      console.error("Save error:", err);
      if (
        err.response?.status === 400 &&
        err.response?.data?.msg === "Open ticket already exists"
      ) {
        showSnackbar(
          "An open ticket already exists for this branch. Please close it first.",
          "error",
        );
      } else {
        showSnackbar(err.response?.data?.message || "Save Failed", "error");
      }
    }
  }, [
    newTicket,
    conversationInput,
    commentsArray,
    currentUsername,
    currentRole,
    connectionStatus,
    editId,
    addNewComment,
    formatCommentsToString,
    showSnackbar,
    fetchTickets,
  ]);

  const handleUpdateProfile = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setIsUpdating(true);
    try {
      const payload = {
        primaryContact: editForm.primaryContact,
      };

      const response = await axios.put(`${API_URL}/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showSnackbar("Profile updated successfully", "success");
      setIsProfileOpen(false);
      fetchProfile();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Update failed", "error");
    } finally {
      setIsUpdating(false);
    }
  }, [editForm, showSnackbar, fetchProfile]);

  const handleUpdatePassword = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Validate new password
    if (passwordForm.newPassword) {
      const validation = validatePassword(passwordForm.newPassword);
      if (!validation.isValid) {
        showSnackbar(`Password requirements: ${validation.errors.join(", ")}`, "error");
        return;
      }
    }

    // Check if new password matches confirm password
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showSnackbar("New password and confirm password do not match", "error");
      return;
    }

    // Check if new password is same as old password
    if (passwordForm.oldPassword === passwordForm.newPassword) {
      showSnackbar("New password cannot be the same as old password", "error");
      return;
    }

    // Check if old password is provided
    if (!passwordForm.oldPassword) {
      showSnackbar("Old password is required to change password", "error");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const payload = {
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
      };

      const response = await axios.put(`${API_URL}/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showSnackbar("Password updated successfully", "success");
      setIsPasswordDialogOpen(false);
      clearPasswordFields();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Password update failed", "error");
    } finally {
      setIsUpdatingPassword(false);
    }
  }, [passwordForm, showSnackbar, clearPasswordFields]);

  const paginatedTickets = useMemo(() => {
    return filteredTickets.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredTickets, page, rowsPerPage]);

  const getAvatarColor = useCallback((role) => {
    switch (role) {
      case "branch":
        return "#FFD600";
      case "admin":
        return "#2196F3";
      case "telecom":
        return "#9C27B0";
      case "technician":
        return "#4CAF50";
      default:
        return "#999999";
    }
  }, []);

  // Logout handler
  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    navigate("/login");
  }, [navigate]);

  // Calculate table height for mobile
  const toolbarHeight = 120;
  const paginationHeight = 60;
  const headerHeight = 80;

  const getTableHeight = () => {
    const availableHeight = window.innerHeight - (toolbarHeight + paginationHeight + headerHeight);
    return `${availableHeight}px`;
  };

  function formatDuration(totalMinutes) {
    const days = Math.floor(totalMinutes / (24 * 60));
    totalMinutes %= 24 * 60;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = (totalMinutes % 60).toFixed(2);

    let result = "";
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;

    return result.trim() || "0m";
  }

  function formatThis(dateValue) {
    if (!dateValue) return "—";
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(",", "");
  }

  // Check if status is Active - to disable Ok option
  const isConnectionActive = connectionStatus?.toLowerCase() === "active";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 1 }}>
      <Container
        maxWidth={false}
        sx={{
          width: { xs: "100%", md: "calc(100% - 168px)" },
          ml: { xs: 0, md: "84px" },
          mr: { xs: 0, md: "84px" },
          px: { xs: 1, md: 0.5 },
        }}
      >
        {/* HEADER */}
        <Paper
          sx={{
            p: 1,
            mt: 1,
            mb: 0.5,
            ...whiteCard,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                bgcolor: "#FFD600",
                color: "#000000",
                border: "1px solid #e0e0e0",
              }}
            >
              B
            </Avatar>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight={600}
              sx={{ color: "#000000" }}
            >
              Branch
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {/* SYSTEM STATUS BUTTON */}
            <Tooltip title="System Status">
              <IconButton
                onClick={() => setIsSystemDialogOpen(true)}
                sx={{
                  color: getSystemStatusColor(systemStatus),
                  backgroundColor: "#ffffff",
                  border: "1px solid #e0e0e0",
                  position: "relative",
                  "&::after": systemStatus?.toLowerCase() !== "active" ? {
                    content: '""',
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: getSystemStatusColor(systemStatus),
                    animation: "pulse 1.5s infinite",
                  } : {},
                }}
              >
                {getSystemStatusIcon(systemStatus)}
              </IconButton>
            </Tooltip>

            <Tooltip title={`Network: ${connectionStatus}`}>
              <IconButton
                onClick={() => setIsStatusDialogOpen(true)}
                sx={{
                  color: getStatusColor(),
                  backgroundColor: "#ffffff",
                  border: "1px solid #e0e0e0",
                }}
              >
                {connectionStatus === "Down" ? (
                  <WifiOffIcon sx={{ fontSize: 24, color: "#ff0000" }} />
                ) : (
                  <WifiIcon />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Station Profile">
              <IconButton
                onClick={() => setIsProfileOpen(true)}
                sx={{
                  ...yellowButton,
                  background: "transparent",
                  border: "1px solid #FFD600",
                }}
              >
                <AccountIcon />
              </IconButton>
            </Tooltip>
            {isMobile ? (
              <Tooltip title="Logout">
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: "#ff0000",
                    border: "1.5px solid #d60202",
                    bgcolor: "rgba(255, 0, 0, 0.05)",
                    transition: "0.3s",
                    "&:hover": {
                      color: "#ff0000",
                      border: "1px solid #ff4444",
                      bgcolor: "rgba(255, 0, 0, 0.1)",
                      boxShadow: "0 0 15px 2px rgba(255, 68, 68, 0.6)",
                    },
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                onClick={handleLogout}
                startIcon={<LogoutIcon sx={{ color: "#ff0000" }} />}
                sx={{
                  color: "#ff4444",
                  border: "1px solid #e0e0e0",
                  bgcolor: "rgba(255, 0, 0, 0.05)",
                  transition: "0.3s",
                  "&:hover": {
                    color: "#ff0000",
                    border: "1px solid #ff4444",
                    bgcolor: "rgba(255, 0, 0, 0.1)",
                    boxShadow: "0 0 15px 2px rgba(255, 68, 68, 0.6)",
                  },
                }}
              >
                Logout
              </Button>
            )}
          </Stack>
        </Paper>

        {/* ACTION TOOLBAR */}
        <Paper
          sx={{
            p: 1,
            mb: 0,
            ...whiteCard,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexGrow: 1,
              minWidth: isMobile ? "100%" : "200px",
              mb: 0,
            }}
          >
            {isMobile ? (
              <IconButton
                onClick={handleOpenCreate}
                sx={{
                  ...yellowButton,
                  borderRadius: 1,
                  p: 1,
                  width: 40,
                  height: 40,
                }}
              >
                <AddIcon />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{ ...yellowButton, whiteSpace: "nowrap", width: 180 }}
              >
                New Ticket
              </Button>
            )}

            <TextField
              placeholder="Search tickets..."
              size="small"
              sx={{
                ...whiteInput,
                width: isMobile ? "100%" : 300,
                flexGrow: isMobile ? 1 : 0,
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Typography
              variant="caption"
              sx={{
                color: "#000000",
                fontWeight: "bold",
                border: "1px solid #e0e0e0",
                px: 1,
                py: 0.5,
                borderRadius: "4px",
                bgcolor: "#ffffff",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
              }}
            >
              {filteredTickets.length}
            </Typography>
            <Tooltip title="Export Excel">
              <IconButton
                onClick={downloadExcel}
                sx={{
                  ...yellowButton,
                  background: "transparent",
                  border: "1px solid #FFD600",
                }}
              >
                <GridOnIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexShrink: 0,
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "flex-start" : "flex-end",
            }}
          >
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ ...whiteInput, width: isMobile ? "48%" : 120 }}
            >
              <MenuItem value="All">All State</MenuItem>
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </TextField>

            <Tooltip title="Refresh Logs">
              <IconButton
                onClick={() => {
                  fetchTickets();
                  fetchProfile();
                  fetchLastUpdate();
                }}
                sx={{
                  ...yellowButton,
                  background: "transparent",
                  border: "1px solid #FFD600",
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: "bold",
                border: "1px solid #ddd",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: "#f5f5f5",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              Updated : {formatThis(lastUpdate)}
            </Typography>
          </Stack>
        </Paper>

        {/* LOG TABLE */}
        <Paper
          sx={{
            ...whiteCard,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <TableContainer
            sx={{
              height: getTableHeight(),
              maxHeight: getTableHeight(),
              overflow: "auto",
              "&::-webkit-scrollbar": {
                height: "8px",
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f5f5f5",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#FFD600",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#ffca00",
              },
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  {[
                    "Network",
                    "Ticket",
                    "CCTNumber",
                    "Opened at",
                    "Closed at",
                    "Technician",
                    "Tech.contact",
                    "Edit Ticket",
                    "Actions",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        bgcolor: "#f8f9fa",
                        color: "#000000",
                        fontWeight: "bold",
                        borderBottom: "2px solid #FFD600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTickets.map((t) => {
                  const comments = parseCommentsToArray(t.comment);
                  const lastComment = comments[comments.length - 1];
                  return (
                    <TableRow key={t._id} hover>
                      <TableCell
                        sx={{
                          tableCell,
                          fontWeight: "bold",
                          color:
                            t.status === "Active"
                              ? "#00e676"
                              : t.status === "Intermittent"
                                ? "#ff9800"
                                : "#ff4444",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.status}
                      </TableCell>
                      <TableCell
                        sx={{
                          tableCell,
                          fontWeight: "bold",
                          color:
                            t.ticketStatus === "Open" ? "#ff4444" : "#00e676",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.ticketStatus}
                      </TableCell>

                      <TableCell sx={{ ...tableCell, whiteSpace: "nowrap" }}>
                        {t.CCTNumber}
                      </TableCell>

                      <TableCell sx={{ ...tableCell, whiteSpace: "nowrap" }}>
                        {new Date(t.openedAt).toLocaleString()}
                      </TableCell>

                      <TableCell sx={{ ...tableCell, whiteSpace: "nowrap" }}>
                        {t.closedAt
                          ? new Date(t.closedAt).toLocaleString()
                          : "Pending"}
                      </TableCell>

                      <TableCell sx={{ ...tableCell, whiteSpace: "nowrap" }}>
                        {t.assignedTechnician}
                      </TableCell>

                      <TableCell sx={{ ...tableCell, whiteSpace: "nowrap" }}>
                        {t.techprimaryContact}
                      </TableCell>

                      <TableCell
                        sx={{
                          color: "#333333",
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Tooltip title="View/Update Conversation">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(t)}
                              disabled = {t?.ticketStatus?.toLowerCase() !== "open"}
                              sx={{
                                color: "#000000",
                                background: "#fdcf00",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title={lastComment?.message || "No comments"}
                            arrow
                          >
                            <span
                              style={{
                                maxWidth: "250px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {lastComment?.message?.substring(0, 100) ||
                                "No comments"}
                              ...
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {t.ticketStatus === "Open" ? (
                            <Tooltip title="Protocol Pending">
                              <Box sx={{ px: 1 }}>
                                <CloseIcon sx={{ color: "#ff0000" }} />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Protocol Finalized">
                              <Box sx={{ px: 1 }}>
                                <CloseIcon sx={{ color: "#44ff44" }} />
                              </Box>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredTickets.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100, 200]}
            sx={{
              color: "#000000",
              borderTop: "1px solid #e0e0e0",
            }}
          />
        </Paper>
      </Container>

      {/* SYSTEM STATUS DIALOG - FIXED FOR BIG DEVICES */}
      <Dialog
        open={isSystemDialogOpen}
        onClose={() => setIsSystemDialogOpen(false)}
        PaperProps={{
          sx: {
            ...whiteCard,
            border: "2px solid #FFD600",
            borderRadius: "16px",
            maxWidth: "500px",
            width: { xs: "90%", sm: "auto" },
            maxHeight: "80vh",
          }
        }}
        fullWidth
      >
        <DialogTitle sx={{
          color: "#ffbc02",
          fontWeight: "bold",
          fontSize: { xs: "0.9rem", sm: "1.1rem" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 2 }
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getSystemStatusIcon(systemStatus)}
            System Status
          </Box>
          <IconButton
            size="small"
            onClick={() => setIsSystemDialogOpen(false)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              color: "#4d4646",
              border: "1px solid #e0e0e0",
              bgcolor: "rgba(255,0,0,0.05)",
              "&:hover": {
                color: "#ff0000",
                bgcolor: "rgba(255,0,0,0.1)",
                boxShadow: "0 0 8px red"
              }
            }}
          >
            ✖
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {/* Current System Status */}
          <Box sx={{
            mb: 3,
            p: 2,
            bgcolor: getSystemStatusColor(systemStatus) + "20",
            borderRadius: 2,
            borderLeft: `4px solid ${getSystemStatusColor(systemStatus)}`
          }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#000000", mb: 1 }}>
              Current Status:
              <span style={{ color: getSystemStatusColor(systemStatus), marginLeft: "8px" }}>
                {systemStatus?.toUpperCase() || "UNKNOWN"}
              </span>
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <SpeedIcon sx={{ color: "#666666", fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: "#666666" }}>
                Duration: <strong>{formatDuration(duration)}</strong>
              </Typography>
              {duration > 0 && (
                <Typography variant="caption" sx={{ color: "#ff9800" }}>
                  ({duration} minutes)
                </Typography>
              )}
            </Box>
            {systemProblem && systemProblem !== "Ok" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, p: 1, bgcolor: "#fff3e0", borderRadius: 1 }}>
                <WarningIcon sx={{ color: "#ff9800", fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: "#666666" }}>
                  Problem: <strong>{systemProblem}</strong>
                </Typography>
              </Box>
            )}
          </Box>

          {/* AI Suggestion */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#ffbc02", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="span" sx={{ fontSize: "1.2rem" }}>💡</Typography>
              Recommendation
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: 2, border: "1px solid #e0e0e0" }}>
              <Typography variant="body2" sx={{ color: "#333333", lineHeight: 1.6 }}>
                {suggestion}
              </Typography>
            </Paper>
          </Box>

          {/* Quick Actions */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#666666", mb: 1 }}>
              Quick Actions:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setIsSystemDialogOpen(false);
                  window.location.reload();
                }}
                sx={{ borderColor: "#FFD600", color: "#000000", "&:hover": { bgcolor: "#FFD60020" } }}
              >
                Refresh Status
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setIsSystemDialogOpen(false);
                  setIsStatusDialogOpen(true);
                }}
                sx={{ borderColor: "#FFD600", color: "#000000", "&:hover": { bgcolor: "#FFD60020" } }}
              >
                Check Network
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* TICKET DIALOG */}
      <Dialog
        open={isTicketOpen}
        onClose={() => setIsTicketOpen(false)}
        PaperProps={{
          sx: {
            ...whiteCard,
            border: "2px solid #FFD600",
            borderRadius: "16px",
            maxWidth: "800px",
            width: "100%",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            color: "#ffbc02",
            fontWeight: "bold",
            fontSize: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            px: 2,
          }}
        >
          {editId ? "EDIT TICKET/LOG" : "CREATE NEW TICKET"}
          <IconButton
            size="small"
            onClick={() => setIsTicketOpen(false)}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              color: "#4d4646",
              border: "1px solid #e0e0e0",
              bgcolor: "rgba(255,0,0,0.05)",
              p: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                color: "#ff0000",
                bgcolor: "rgba(255,0,0,0.1)",
                boxShadow: "0 0 8px red",
              },
            }}
          >
            ✖
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 1,
          }}
        >
          {/* CCTNumber Field - Column with icon and enhanced visual */}
{editId && (
  <Box
    sx={{
      mt: 0.5,
      mb: 1.5,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.75,
      width: "100%",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          color: "#FFD600",
          fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <span style={{ fontSize: "1.1rem" }}>📋</span> CCT Number
      </Typography>
      {newTicket.CCTNumber && (
        <Box
          sx={{
            ml: "auto",
            px: 1,
            py: 0.25,
            bgcolor: "#e8f5e9",
            borderRadius: "4px",
            fontSize: "0.6rem",
            color: "#2e7d32",
            fontWeight: "bold",
          }}
        >
          ✓ Entered
        </Box>
      )}
    </Box>

    <TextField
      size="small"
      fullWidth
      value={newTicket.CCTNumber || ""}
      onChange={(e) =>
        setNewTicket({
          ...newTicket,
          CCTNumber: e.target.value,
        })
      }
      sx={{
        ...whiteInput,
        width: "100%",
        "& .MuiInputBase-input": {
          fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
          padding: { xs: "10px 14px", sm: "12px 16px", md: "14px 18px" },
          color: "#000000",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: { xs: "8px", sm: "10px", md: "12px" },
          backgroundColor: "#ffffff",
          transition: "all 0.2s ease",
          "& fieldset": {
            borderColor: "#e0e0e0",
            borderWidth: "1.5px",
          },
          "&:hover fieldset": {
            borderColor: "#FFD600",
            borderWidth: "2px",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#FFD600",
            borderWidth: "2px",
            boxShadow: "0 0 0 3px rgba(255, 214, 0, 0.1)",
          },
        },
        "& .MuiInputLabel-root": {
          display: "none",
        },
        "& .MuiFormHelperText-root": {
          display: "none",
        },
      }}
      placeholder="Enter CCT number"
      variant="outlined"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography
              sx={{
                color: "#999",
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem" },
                fontWeight: 500,
              }}
            >
              #
            </Typography>
          </InputAdornment>
        ),
        endAdornment: newTicket.CCTNumber && (
          <InputAdornment position="end">
            <Typography
              sx={{
                color: "#4caf50",
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
            >
              ✓
            </Typography>
          </InputAdornment>
        ),
      }}
    />
  </Box>
)}

          {commentsArray.length > 0 ? (
            <Paper
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 1,
                bgcolor: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <Stack spacing={1}>
                {commentsArray.map((comment) => (
                  <Box
                    key={comment.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        comment.role === "branch" ? "flex-end" : "flex-start",
                    }}
                  >
                    <Box sx={{ maxWidth: "80%" }}>
                      {editingCommentId === comment.id ? (
                        <Paper sx={{ p: 1, border: "2px solid #FFD600" }}>
                          <TextField
                            inputRef={editInputRef}
                            multiline
                            rows={2}
                            fullWidth
                            value={editMessageText}
                            onChange={handleEditInputChange}
                            onKeyPress={(e) => handleEditKeyPress(e, comment.id)}
                            autoFocus
                          />
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                            <Button size="small" onClick={cancelEditing}>Cancel</Button>
                            <Button size="small" onClick={() => handleSaveEdit(comment.id)} sx={yellowButton}>Save</Button>
                          </Box>
                        </Paper>
                      ) : (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                            <Avatar sx={{ width: 20, height: 20, fontSize: "0.7rem", bgcolor: getAvatarColor(comment.role) }}>
                              {comment.username.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                              {comment.username} ({comment.role})
                            </Typography>
                            {comment.role === "branch" && (
                              <IconButton size="small" onClick={(e) => handleCommentMenuOpen(e, comment.id)} sx={{ p: 0 }}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          <Paper sx={{ p: 1, bgcolor: comment.role === "branch" ? "#FFD60020" : "#fff", borderRadius: comment.role === "branch" ? "12px 12px 4px 12px" : "12px 12px 12px 4px" }}>
                            <Typography variant="body2">{comment.message}</Typography>
                            <Typography variant="caption" sx={{ color: "#666" }}>{comment.timestamp}</Typography>
                          </Paper>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </Stack>
            </Paper>
          ) : (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" sx={{ color: "#999" }}>No messages yet</Typography>
            </Box>
          )}

          {(!editId || (editId && newTicket.ticketStatus === "Open")) && (
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #e0e0e0", display: "flex", flexDirection: "column", gap: 1 }}>
              {newTicket.ticketStatus !== "Closed" && (
                <TextField
                  placeholder="Type message..."
                  multiline
                  rows={2}
                  fullWidth
                  value={conversationInput}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => { handleSendMessage(); handleSaveTicket(); }} sx={{ bgcolor: "#FFD600", "&:hover": { bgcolor: "#ffcc00" } }}>
                          <SendIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label={connectionStatus} size="small" sx={{ bgcolor: getStatusColor(), color: "#000000", fontWeight: "bold" }} />
                  <Chip label={newTicket.ticketStatus} size="small" />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button onClick={handleSaveTicket} size="small" variant="contained" startIcon={editId ? <SaveIcon /> : <AddIcon />} sx={{ ...yellowButton, borderRadius: "50px", px: 2, py: 0.5, minHeight: "28px", fontSize: "0.75rem", textTransform: "none" }}>
                    {editId ? "UPDATE" : "CREATE"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Comment Action Menu */}
      <Menu anchorEl={commentMenuAnchor} open={Boolean(commentMenuAnchor)} onClose={handleCommentMenuClose}>
        <MenuItem onClick={() => { const comment = commentsArray.find(c => c.id === selectedCommentId); if (comment) startEditing(comment); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit Comment</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteComment}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete Comment</ListItemText>
        </MenuItem>
      </Menu>

      {/* PROFILE DIALOG - Updated to remove password fields */}
      <Dialog
        open={isProfileOpen}
        onClose={() => {
          setIsProfileOpen(false);
        }}
        PaperProps={{ sx: { ...whiteCard, border: "2px solid #FFD600", borderRadius: "16px" } }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ color: "#ffbc02", fontWeight: "bold", fontSize: "1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          USER_PROFILE
          <IconButton
            size="small"
            onClick={() => {
              setIsProfileOpen(false);
            }}
            sx={{ width: 36, height: 36, borderRadius: "50%", color: "#4d4646", border: "1px solid #e0e0e0", bgcolor: "rgba(255,0,0,0.05)", "&:hover": { color: "#ff0000", bgcolor: "rgba(255,0,0,0.1)", boxShadow: "0 0 8px red" } }}
          >
            ✖
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {profile && (
            <Stack spacing={2} sx={{ mb: 3 }}>
              {["name", "username", "serviceNumber","wanIP"].map((f) => (
                <Box key={f} sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e0e0e0", pb: 1 }}>
                  <Typography variant="caption" sx={{ color: "#666666", fontWeight: "bold" }}>{f.toUpperCase()}</Typography>
                  <Typography variant="body2" sx={{ color: "#000000" }}>{profile[f] || "N/A"}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0", pb: 1 }}>
                <Typography variant="caption" sx={{ color: "#666666", fontWeight: "bold" }}>LOCATION</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "#000000" }}>
                    {getLocationDisplay(profile)}
                  </Typography>
                  <Tooltip title={isMobile ? "Update Location" : "Location updates only available on mobile devices"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleUpdateLocationClick}
                        disabled={!isMobile || isUpdatingLocation || isGettingLocation}
                        sx={{ p: 0.5, backgroundColor: isMobile ? "#FFD600" : "#e0e0e0", color: "#000000" }}
                      >
                        {isUpdatingLocation || isGettingLocation ? <CircularProgress size={14} sx={{ color: "#000000" }} /> : <MyLocationIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Stack>
          )}
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2}>
            <TextField
              label="Primary Contact"
              name="primaryContact"
              fullWidth
              size="small"
              sx={whiteInput}
              value={editForm.primaryContact}
              onChange={handleProfileChange}
            />
          </Stack>
          
          {/* Change Password Button */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => {
                setIsProfileOpen(false);
                setIsPasswordDialogOpen(true);
                clearPasswordFields();
              }}
              sx={{
                ...yellowButton,
                background: "transparent",
                border: "2px solid #FFD600",
                borderRadius: "50px",
                px: 3,
                py: 1,
                width: "100%",
                "&:hover": {
                  background: "#FFD60020",
                  border: "2px solid #ffcc00",
                }
              }}
            >
              Change Password
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={() => {
              setIsProfileOpen(false);
            }}
            sx={{ color: "#666666", borderRadius: "50px", px: 3.2, py: 0.8, fontWeight: 700, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateProfile}
            startIcon={<SaveIcon />}
            sx={{ ...yellowButton, borderRadius: "50px", px: 3.2, py: 0.8, minHeight: 32 }}
            disabled={isUpdating}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* CHANGE PASSWORD DIALOG - New separate dialog */}
      <Dialog
        open={isPasswordDialogOpen}
        onClose={() => {
          setIsPasswordDialogOpen(false);
          clearPasswordFields();
        }}
        PaperProps={{ 
          sx: { 
            ...whiteCard, 
            border: "2px solid #FFD600", 
            borderRadius: "16px",
            maxWidth: "450px",
            width: "100%",
          } 
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ 
          color: "#ffbc02", 
          fontWeight: "bold", 
          fontSize: "1.2rem", 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VpnKeyIcon sx={{ color: "#FFD600" }} />
            Change Password
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setIsPasswordDialogOpen(false);
              clearPasswordFields();
            }}
            sx={{ 
              width: 36, 
              height: 36, 
              borderRadius: "50%", 
              color: "#4d4646", 
              border: "1px solid #e0e0e0", 
              bgcolor: "rgba(255,0,0,0.05)", 
              "&:hover": { 
                color: "#ff0000", 
                bgcolor: "rgba(255,0,0,0.1)", 
                boxShadow: "0 0 8px red" 
              } 
            }}
          >
            ✖
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, pb: 1 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="caption" sx={{ color: "#666666", fontWeight: "bold", mb: 0.5, display: "block" }}>
                Old Password
              </Typography>
              <TextField
                type={showOldPassword ? "text" : "password"}
                name="oldPassword"
                fullWidth
                size="small"
                sx={whiteInput}
                value={passwordForm.oldPassword || ""}
                onChange={handlePasswordChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                        size="small"
                      >
                        {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter current password"
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "#666666", fontWeight: "bold", mb: 0.5, display: "block" }}>
                New Password
              </Typography>
              <TextField
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                fullWidth
                size="small"
                sx={whiteInput}
                value={passwordForm.newPassword || ""}
                onChange={handlePasswordChange}
                error={passwordErrors.length > 0 && passwordForm.newPassword !== ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        size="small"
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter new password"
              />
              {passwordErrors.length > 0 && passwordForm.newPassword !== "" && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  Password must contain: {passwordErrors.join(", ")}
                </FormHelperText>
              )}
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "#666666", fontWeight: "bold", mb: 0.5, display: "block" }}>
                Confirm New Password
              </Typography>
              <TextField
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                fullWidth
                size="small"
                sx={whiteInput}
                value={passwordForm.confirmPassword || ""}
                onChange={handlePasswordChange}
                error={passwordForm.confirmPassword !== "" && passwordForm.newPassword !== passwordForm.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Confirm new password"
              />
              {passwordForm.confirmPassword !== "" && passwordForm.newPassword !== passwordForm.confirmPassword && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  Passwords do not match
                </FormHelperText>
              )}
            </Box>

            <Typography variant="caption" sx={{ color: "#666666", mt: 0.5, fontSize: "0.65rem" }}>
              Password requirements: min 8 chars, uppercase, lowercase, number, special character
            </Typography>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          justifyContent: "flex-end", 
          gap: 2, 
          borderTop: "1px solid #e0e0e0",
          flexWrap: "wrap",
        }}>
          <Button
            onClick={() => {
              setIsPasswordDialogOpen(false);
              clearPasswordFields();
            }}
            sx={{ color: "#666666", borderRadius: "50px", px: 3.2, py: 0.8, fontWeight: 700, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdatePassword}
            startIcon={isUpdatingPassword ? <CircularProgress size={16} sx={{ color: "#000000" }} /> : <SaveIcon />}
            sx={{ ...yellowButton, borderRadius: "50px", px: 3.2, py: 0.8, minHeight: 32 }}
            disabled={isUpdatingPassword || 
              !passwordForm.oldPassword || 
              !passwordForm.newPassword || 
              !passwordForm.confirmPassword ||
              passwordForm.newPassword !== passwordForm.confirmPassword ||
              (passwordForm.newPassword !== "" && passwordErrors.length > 0)}
          >
            {isUpdatingPassword ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRMATION DIALOG FOR LOCATION UPDATE */}
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)} PaperProps={{ sx: { ...whiteCard, border: "2px solid #FFD600", borderRadius: "16px" } }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ color: "#ffbc02", fontWeight: "bold" }}>Confirm Location Update</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#000000", mb: 2 }}>Are you sure you want to update your station's location to the current location?</Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>This will update your station's location in the database.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setIsConfirmDialogOpen(false)} sx={{ color: "#666666" }}>Cancel</Button>
          <Button onClick={confirmAndUpdateLocation} variant="contained" disabled={isUpdatingLocation || isGettingLocation} sx={yellowButton}>
            {isUpdatingLocation || isGettingLocation ? "Updating..." : "Confirm Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONNECTION STATUS DIALOG - FIXED FOR BIG DEVICES */}
      <Dialog
        open={isStatusDialogOpen}
        onClose={() => setIsStatusDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            ...whiteCard,
            border: "2px solid #FFD600",
            borderRadius: "16px",
            width: { xs: "90%", sm: "500px" },
            maxHeight: { xs: "80vh", sm: "90vh" },
            overflow: "hidden",
          }
        }}
      >
        <DialogTitle sx={{
          color: "#ffbc02",
          fontWeight: "bold",
          fontSize: { xs: "0.9rem", sm: "1.1rem" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: { xs: 1, sm: 1.5 },
          px: { xs: 1.5, sm: 2 },
          borderBottom: "1px solid #e0e0e0"
        }}>
          Network Status
          <IconButton
            size="small"
            onClick={() => setIsStatusDialogOpen(false)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              color: "#4d4646",
              border: "1px solid #e0e0e0",
              bgcolor: "rgba(255,0,0,0.05)",
              "&:hover": {
                color: "#ff0000",
                bgcolor: "rgba(255,0,0,0.1)",
                boxShadow: "0 0 8px red"
              }
            }}
          >
            ✖
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          p: { xs: 1.5, sm: 2 },
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f5f5f5",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#FFD600",
            borderRadius: "3px",
          },
        }}>
          {/* TOP ROW - Network Topology */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: "0.7rem", color: "#ffbc02", mb: 1, fontWeight: "bold", textAlign: "center" }}>
              🔌 NETWORK TOPOLOGY
            </Typography>

            {(() => {
              const recentHistory = profile?.pingHistory || "0000000000000";
              const pingResult = profile?.pingResult || "000";
              const switchStatus = pingResult[0] === '1';
              const routerStatus = pingResult[1] === '1';
              const modemStatus = pingResult[2] === '1';

              return (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5, bgcolor: '#2c2c2c', borderRadius: 1, borderLeft: `3px solid ${switchStatus ? '#4CAF50' : '#f44336'}` }}>
                    <Typography sx={{ fontSize: '0.65rem', color: "#ffbc02", fontWeight: "bold" }}>🔌 SWITCH</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: switchStatus ? '#4CAF50' : '#f44336' }} />
                      <Typography sx={{ fontSize: '0.6rem', color: switchStatus ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>{switchStatus ? 'Online' : 'Offline'}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.3, height: '8px' }}>
                    <Box sx={{ width: 2, height: '100%', bgcolor: switchStatus && routerStatus ? '#4CAF50' : '#f44336' }} />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5, bgcolor: '#2c2c2c', borderRadius: 1, borderLeft: `3px solid ${routerStatus ? '#4CAF50' : '#f44336'}` }}>
                    <Typography sx={{ fontSize: '0.65rem', color: "#ffbc02", fontWeight: "bold" }}>🌐 ROUTER</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: routerStatus ? '#4CAF50' : '#f44336' }} />
                      <Typography sx={{ fontSize: '0.6rem', color: routerStatus ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>{routerStatus ? 'Online' : 'Offline'}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.3, height: '8px' }}>
                    <Box sx={{ width: 2, height: '100%', bgcolor: routerStatus && modemStatus ? '#4CAF50' : '#f44336' }} />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5, bgcolor: '#2c2c2c', borderRadius: 1, borderLeft: `3px solid ${modemStatus ? '#4CAF50' : '#f44336'}` }}>
                    <Typography sx={{ fontSize: '0.65rem', color: "#ffbc02", fontWeight: "bold" }}>📡 MODEM</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: modemStatus ? '#4CAF50' : '#f44336' }} />
                      <Typography sx={{ fontSize: '0.6rem', color: modemStatus ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>{modemStatus ? 'Online' : 'Offline'}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 0.5, textAlign: 'center', bgcolor: '#1a1a1a', p: 0.3, borderRadius: 1 }}>
                    <Typography sx={{ color: '#ffd000', fontSize: '0.7rem', fontFamily: 'monospace', fontWeight: "bold" }}>Recent statuses: {recentHistory}</Typography>
                  </Box>
                </Box>
              );
            })()}
          </Box>

          {/* PROBLEM TYPE - Horizontal with OK first, then Company, ETC/ELPA, ETC, ELPA, Other */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: "0.7rem", color: "#666", mb: 1, fontWeight: "bold" }}>
              PROBLEM FROM
            </Typography>

            {/* Auto-select OK if system is Active, or set default */}
            {useEffect(() => {
              if (isConnectionActive && problemType !== "OK") {
                setProblemType("OK");
              }
            }, [isConnectionActive])}

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
              {(() => {
                // Build the problem types in the exact order: OK, Company, ETC/ELPA, ETC, ELPA, Other
                const orderedTypes = [];

                // 1. OK - always first
                orderedTypes.push("OK");

                // 2. Company (if exists and not already in list)
                if (profile?.company && !orderedTypes.includes(profile.company)) {
                  orderedTypes.push(profile.company);
                }

                // 3. ETC/ELPA (combined option)
                orderedTypes.push("ETC/ELPA");

                // 4. ETC
                orderedTypes.push("ETC");

                // 5. ELPA
                orderedTypes.push("ELPA");

                // 6. Other
                orderedTypes.push("Other");

                // Remove duplicates while preserving order
                const problemTypes = [...new Set(orderedTypes)];

                const isCompanySelected = problemType === profile?.company;
                const isOkSelected = problemType === "OK";

                return problemTypes.map((type) => {
                  const isCompanyProblem = profile?.company === type;
                  const isETCELPA = type === "ETC/ELPA";
                  const isOk = type === "OK";
                  const isCompany = type === profile?.company;
                  const isEtc = type === "ETC";
                  const isElpa = type === "ELPA";
                  const isOther = type === "Other";

                  // Determine if this option should be disabled
                  let isDisabled = false;

                  // 🔥 MAIN LOGIC:
                  // If system is Active, ONLY OK is enabled and auto-selected
                  if (isConnectionActive) {
                    if (!isOk) {
                      isDisabled = true;
                    }
                  }
                  // If problem is OK or Company: disable ALL other options
                  else if (isOkSelected || isCompanySelected) {
                    if (isOkSelected && !isOk) {
                      isDisabled = true;
                    } else if (isCompanySelected && !isCompany) {
                      isDisabled = true;
                    }
                  }
                  // If problem is NOT OK or Company (i.e., ETC/ELPA, ETC, ELPA, Other):
                  // Disable OK and Company, but enable the rest freely
                  else {
                    if (isOk || isCompany) {
                      isDisabled = true;
                    }
                    // All other options (ETC/ELPA, ETC, ELPA, Other) are enabled
                  }

                  // When problem is saved and not OK/Company, disable all except the saved one
                  if (isProblemSaved && !isOkSelected && !isCompanySelected && type !== problemType) {
                    isDisabled = true;
                  }

                  // Check if this option should be highlighted/checked
                  const isActive = problemType === type;

                  // Determine label display
                  let displayLabel = type;
                  if (isETCELPA) {
                    displayLabel = "ETC/ELPA";
                  }

                  return (
                    <Box key={type} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <input
                        type="radio"
                        id={`problem_${type.toLowerCase().replace(/[\/&]/g, '_')}`}
                        name="problemType"
                        value={type}
                        checked={isActive}
                        onChange={handleProblemTypeChange}
                        disabled={isDisabled}
                        style={{
                          accentColor: "#FFD600",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          transform: "scale(0.9)",
                          opacity: isDisabled && isActive ? 0.7 : (isDisabled ? 0.4 : 1),
                        }}
                      />
                      <label
                        htmlFor={`problem_${type.toLowerCase().replace(/[\/&]/g, '_')}`}
                        style={{
                          fontSize: "0.75rem",
                          color: isDisabled && isActive ? "#000000" : (isDisabled ? "#999" : (isActive ? "#000000" : "#333333")),
                          fontWeight: isActive ? "bold" : 400,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          borderBottom: isActive ? "2px solid #FFD600" : "none",
                          paddingBottom: isActive ? "1px" : "0",
                          opacity: isDisabled && !isActive ? 0.5 : 1,
                        }}
                      >
                        {displayLabel}
                      </label>
                    </Box>
                  );
                });
              })()}
            </Box>

            {/* Status Messages */}
            {problemType === "OK" && (
              <Typography sx={{ fontSize: "0.65rem", color: "#4caf50", mt: 0.5, p: 0.5, bgcolor: "#e8f5e9", borderRadius: 1, textAlign: "center" }}>
                ✅ "OK" selected. All other options are disabled.
              </Typography>
            )}
            {problemType === profile?.company && profile?.company && (
              <Typography sx={{ fontSize: "0.65rem", color: "#ff9800", mt: 0.5, p: 0.5, bgcolor: "#fff3e0", borderRadius: 1, textAlign: "center" }}>
                ✅ Company problem selected. All other options including OK are disabled.
              </Typography>
            )}
            {(problemType === "ETC/ELPA" || problemType === "ETC" || problemType === "ELPA" || problemType === "Other") && (
              <Typography sx={{ fontSize: "0.65rem", color: "#ff9800", mt: 0.5, p: 0.5, bgcolor: "#fff3e0", borderRadius: 1, textAlign: "center" }}>
                ⚠️ "{problemType}" selected. OK and Company options are disabled. You can freely choose among the other options.
              </Typography>
            )}
            {isProblemSaved && problemType !== "OK" && problemType !== profile?.company && (
              <Typography sx={{ fontSize: "0.65rem", color: "#4caf50", mt: 0.5, p: 0.5, bgcolor: "#e8f5e9", borderRadius: 1, textAlign: "center" }}>
                ✓ Problem type saved. Click SAVE again to update.
              </Typography>
            )}
            {isConnectionActive && (
              <Typography sx={{ fontSize: "0.65rem", color: "#ff9800", mt: 0.5, p: 0.5, bgcolor: "#fff3e0", borderRadius: 1, textAlign: "center" }}>
                ✅ System is ACTIVE. "OK" selected automatically. All other options are disabled.
              </Typography>
            )}
          </Box>

          {/* BOTTOM ROW - Two Columns Static */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1.5,
            mb: 1
          }}>

            {/* LEFT COLUMN - AI Suggestion */}
            <Box>
              <Typography sx={{ fontSize: "0.6rem", color: "#ffbc02", mb: 0.5, fontWeight: "bold", display: "flex", alignItems: "center", gap: 0.5 }}>
                🤖 AI SUGGESTION
              </Typography>
              <Paper sx={{ p: 1, bgcolor: "#e8f5e9", borderRadius: 2, border: "1px solid #c8e6c9", minHeight: "100px" }}>
                <Typography variant="body2" sx={{ color: "#1b5e20", lineHeight: 1.4, fontSize: "0.65rem", fontWeight: 400 }}>
                  {profile.suggestion}
                </Typography>
              </Paper>
            </Box>

            {/* RIGHT COLUMN - Status, Duration, Buttons */}
            <Box>
              {/* Status */}
              <Box sx={{ mb: 1, p: 0.75, bgcolor: getStatusColor() + '20', borderRadius: 2, textAlign: 'center', border: `1px solid ${getStatusColor()}` }}>
                <Typography sx={{ fontSize: "0.55rem", color: "#666", fontWeight: "bold" }}>STATUS</Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold", color: getStatusColor() }}>{connectionStatus}</Typography>
              </Box>

              {/* Duration */}
              <Box sx={{ mb: 1, p: 0.75, bgcolor: "#f5f5f5", borderRadius: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: "0.55rem", color: "#666", fontWeight: "bold" }}>DURATION</Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: "bold", color: "#000000" }}>{formatDuration(profile?.duration) || "0m"}</Typography>
              </Box>

              {/* Buttons Row - Create Ticket & Save */}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {(connectionStatus?.toLowerCase() === "down" || connectionStatus?.toLowerCase() === "intermittent") && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: "0.9rem" }} />}
                    onClick={() => { setIsStatusDialogOpen(false); handleOpenCreate(); }}
                    sx={{ ...yellowButton, py: 0.5, px: 1, fontSize: "0.65rem", flex: 1 }}
                  >
                    TICKET
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={handleSaveProblemType}
                  disabled={isProblemSaved && problemType !== "Ok" && problemType !== profile?.company}
                  sx={{ ...yellowButton, py: 0.5, px: 1.5, fontSize: "0.65rem", flex: connectionStatus?.toLowerCase() === "down" || connectionStatus?.toLowerCase() === "intermittent" ? 1 : 'auto' }}
                >
                  SAVE
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ bgcolor: "#FFD600", color: "#000000" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add keyframes for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(1.2);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default BranchDashboard;